import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import minMax from "dayjs/plugin/minMax";
import isBetween from "dayjs/plugin/isBetween";
import { getActivityDate } from "./timeline";
import { canonicalSportForFilter, filterActivitiesByCanonicalSport } from "./sportNormalization";

dayjs.extend(isoWeek);
dayjs.extend(minMax);
dayjs.extend(isBetween);

export const RANGE_PRESETS = [
  { key: "last_4_weeks", label: "Last 4 weeks", unit: "week", amount: 4 },
  { key: "last_12_weeks", label: "Last 12 weeks", unit: "week", amount: 12 },
  { key: "last_6_months", label: "Last 6 months", unit: "month", amount: 6 },
  { key: "last_12_months", label: "Last 12 months", unit: "month", amount: 12 },
  { key: "this_year", label: "This year", unit: "year", amount: 0 },
  { key: "last_year", label: "Last year", unit: "year", amount: -1 },
  { key: "all_time", label: "All time", unit: "all", amount: 0 },
  { key: "custom", label: "Custom range", unit: "custom", amount: 0 },
];

export const GROUPING_OPTIONS = [
  { key: "daily", label: "Daily" },
  { key: "weekly", label: "Weekly" },
  { key: "monthly", label: "Monthly" },
];

export function normalizeSportType(sportOrActivity) {
  return canonicalSportForFilter(
    sportOrActivity && typeof sportOrActivity === "object" ? sportOrActivity : { sport: sportOrActivity }
  );
}

export function filterActivitiesBySport(activities, sportFilter) {
  return filterActivitiesByCanonicalSport(activities, sportFilter);
}

export function buildDateRange(presetKey, activities, customStart, customEnd) {
  const today = dayjs().endOf("day");
  const preset = RANGE_PRESETS.find((p) => p.key === presetKey) || RANGE_PRESETS[0];
  if (preset.key === "custom") {
    const start = customStart ? dayjs(customStart).startOf("day") : null;
    const end = customEnd ? dayjs(customEnd).endOf("day") : null;
    return { start, end, label: "Custom range" };
  }
  if (preset.key === "all_time") {
    const dates = activities.map(getActivityDate).filter(Boolean);
    if (!dates.length) {
      return { start: today.startOf("year"), end: today, label: "All time" };
    }
    return {
      start: dayjs.min(...dates).startOf("day"),
      end: dayjs.max(...dates).endOf("day"),
      label: "All time",
    };
  }
  if (preset.key === "this_year") {
    return {
      start: today.startOf("year"),
      end: today,
      label: "This year",
    };
  }
  if (preset.key === "last_year") {
    const lastYear = today.subtract(1, "year");
    return {
      start: lastYear.startOf("year"),
      end: lastYear.endOf("year"),
      label: "Last year",
    };
  }
  const end = today;
  const start = today.subtract(preset.amount, preset.unit).add(1, "day").startOf("day");
  return { start, end, label: preset.label };
}

export function defaultGroupingForRange(start, end) {
  if (!start || !end) return "weekly";
  const days = end.diff(start, "day") + 1;
  if (days <= 31) return "daily";
  if (days <= 180) return "weekly";
  return "monthly";
}

function inRange(date, start, end) {
  return date.isBetween(start, end, null, "[]");
}

export function filterActivitiesByRange(activities, start, end) {
  if (!start || !end) return [];
  const rangeStart = start.startOf("day");
  const rangeEnd = end.endOf("day");
  return activities.filter((a) => {
    const d = getActivityDate(a);
    return d && inRange(d, rangeStart, rangeEnd);
  });
}

export function enumeratePeriods(start, end, grouping) {
  if (!start || !end) return [];
  const periods = [];
  let cursor = start.startOf(grouping === "daily" ? "day" : grouping === "weekly" ? "isoWeek" : "month");
  while (cursor.isBefore(end) || cursor.isSame(end, grouping === "daily" ? "day" : grouping === "weekly" ? "isoWeek" : "month")) {
    const periodStart = dayjs.max(
      cursor.startOf(grouping === "daily" ? "day" : grouping === "weekly" ? "isoWeek" : "month"),
      start.startOf("day")
    );
    const periodEnd = dayjs.min(
      cursor.endOf(grouping === "daily" ? "day" : grouping === "weekly" ? "isoWeek" : "month"),
      end.endOf("day")
    );
    const label =
      grouping === "daily"
        ? periodStart.format("MMM D")
        : grouping === "weekly"
          ? periodStart.format("MMM D")
          : periodStart.format("MMM");
    const rangeLabel =
      grouping === "daily"
        ? periodStart.format("MMM D, YYYY")
        : grouping === "weekly"
          ? `${periodStart.format("MMM D")} – ${periodEnd.format("MMM D, YYYY")}`
          : periodStart.format("MMM YYYY");
    periods.push({
      key: grouping === "daily" ? periodStart.format("YYYY-MM-DD") : periodStart.format(grouping === "weekly" ? "GGGG-[W]WW" : "YYYY-MM"),
      start: periodStart,
      end: periodEnd,
      label,
      rangeLabel,
    });
    cursor = cursor.add(1, grouping === "daily" ? "day" : grouping === "weekly" ? "week" : "month");
  }
  return periods;
}

function sumValid(values) {
  const valid = values.filter((v) => Number.isFinite(v));
  if (!valid.length) return { sum: null, count: 0 };
  return { sum: valid.reduce((a, b) => a + b, 0), count: valid.length };
}

export function buildPeriodBuckets(activities, start, end, grouping) {
  const periods = enumeratePeriods(start, end, grouping);
  return periods.map((period) => {
    const inPeriod = activities.filter((a) => {
      const d = getActivityDate(a);
      return d && inRange(d, period.start, period.end);
    });

    const distance = sumValid(inPeriod.map((a) => a.total_distance_m));
    const elapsed = sumValid(inPeriod.map((a) => a.duration_seconds));
    const moving = sumValid(inPeriod.map((a) => a.moving_time_seconds));
    const elevation = sumValid(inPeriod.map((a) => a.elevation_gain_m));
    const calories = sumValid(inPeriod.map((a) => a.calories));
    const workValues = inPeriod
      .filter((a) => Number.isFinite(a.avg_power) && Number.isFinite(a.duration_seconds))
      .map((a) => (a.avg_power * a.duration_seconds) / 1000);
    const work = sumValid(workValues);

    const hasActivities = inPeriod.length > 0;
    return {
      ...period,
      activities: inPeriod,
      activityCount: inPeriod.length,
      distance_m: hasActivities ? (distance.count ? distance.sum : null) : 0,
      elapsed_s: hasActivities ? (elapsed.count ? elapsed.sum : null) : 0,
      moving_s: hasActivities ? (moving.count ? moving.sum : null) : 0,
      elevation_m: hasActivities ? (elevation.count ? elevation.sum : null) : 0,
      calories: hasActivities ? (calories.count ? calories.sum : null) : 0,
      work_kj: hasActivities ? (work.count ? work.sum : null) : 0,
    };
  });
}

export function countWeeksInRange(start, end) {
  if (!start || !end) return 0;
  let cursor = start.startOf("isoWeek");
  let weeks = 0;
  while (cursor.isBefore(end) || cursor.isSame(end, "isoWeek")) {
    weeks += 1;
    cursor = cursor.add(1, "week");
  }
  return weeks;
}

export function activeWeeksInRange(activities, start, end) {
  const weekSet = new Set();
  for (const a of activities) {
    const d = getActivityDate(a);
    if (!d || !inRange(d, start, end)) continue;
    weekSet.add(d.startOf("isoWeek").format("GGGG-[W]WW"));
  }
  return weekSet.size;
}

export function summarizeActivities(activities, start, end) {
  const scoped = filterActivitiesByRange(activities, start, end);
  const distance = sumValid(scoped.map((a) => a.total_distance_m));
  const elapsed = sumValid(scoped.map((a) => a.duration_seconds));
  const moving = sumValid(scoped.map((a) => a.moving_time_seconds));
  const elevation = sumValid(scoped.map((a) => a.elevation_gain_m));
  const calories = sumValid(scoped.map((a) => a.calories));
  const workValues = scoped
    .filter((a) => Number.isFinite(a.avg_power) && Number.isFinite(a.duration_seconds))
    .map((a) => (a.avg_power * a.duration_seconds) / 1000);
  const work = sumValid(workValues);

  const distanceMax = Math.max(...scoped.map((a) => Number(a.total_distance_m) || 0), 0);
  const movingMax = Math.max(...scoped.map((a) => Number(a.moving_time_seconds) || 0), 0);
  const longestDistance = scoped.find((a) => (a.total_distance_m || 0) === distanceMax) || null;
  const longestMoving = scoped.find((a) => (a.moving_time_seconds || 0) === movingMax) || null;

  return {
    scoped,
    activityCount: scoped.length,
    totalDistanceM: distance.count ? distance.sum : null,
    totalElapsedS: elapsed.count ? elapsed.sum : null,
    totalMovingS: moving.count ? moving.sum : null,
    totalElevationM: elevation.count ? elevation.sum : null,
    totalCalories: calories.count ? calories.sum : null,
    totalWorkKj: work.count ? work.sum : null,
    longestDistance,
    longestMoving,
  };
}
